/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState, useCallback, useMemo, useRef} from "react"
import {Checkbox} from "antd"
import {ConfirmPopup, KlassappTable, KlassappTableHeader} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {Icon} from "components/Icon"
import {attendanceService, generalSettingsService} from "services"
import {DailyAttendanceTimeScale} from "types/settings/general-setting"
import {convertTZ, formatDateOnly, getFullName, handleError} from "helpers"
import {formatDailyAttendanceHours} from "helpers/attendance"
import styles from "./../common.module.css"
import dailyAttendanceStyles from "./DailyAttendance.module.css"
import {cloneDeep, get, isEmpty, uniqBy} from "lodash"
import {useCurrentProfilePermissions, useDateTimeFormatters, useModel, useVisible} from "hooks"
import {AttendanceEventType, AttendanceStatus} from "types/attendance"
import {BaseButton} from "components"
import moment from "moment"
import classroomAttendanceStyles from "../ClassroomAttendance/ClassroomAttendance.module.css"
import {DailyAttendancePopup} from "../DailyAttendancePopup"
import {useDebounce} from "react-use"
import {useFilterStorage} from "hooks/useFilterStorage"
import {FilterKey} from "types/filter"
import {YesNo} from "types/common"
import {exportCsv, exportExcel} from "helpers/export-table"
import {ExportFileType} from "components/ui"
import AddManualDailyAttendance from "./AddManualDailyAttendance/AddManualDailyAttendance"
import {v4 as uuid} from "uuid"
import {
    DailyAttendanceFilter,
    DailyAttendanceFilterCheckin,
    DailyAttendanceFilterCheckout
} from "./DailyAttendanceFilter/DailyAttendanceFilter"
import {DAILY_ATTENDANCE_TABS} from "./DailyAttendanceTab"
import {PopupEditMultipleAttendance} from "./PopupEditMultipleAttendance"
import {DailyAttendanceDetailsPopup} from "../DailyAttendanceDetailsPopup"
import {useQuery} from "@tanstack/react-query"
import {getDailyAttendancePermissions} from "helpers/attendance"
import DailyAttendanceCheckInCheckOutPdf, {
    DailyAttendanceCheckInCheckOutPdfHandle
} from "./DailyAttendanceCheckInCheckOutPdf"
import {
    formatCheckInCheckOutDateLabel,
    getSelectedFilterNames,
    mapAttendanceRowToCheckInCheckOutStudent
} from "./mapCheckInCheckOutExport"

export enum FilterEventType {
    Active = "active",
    InSchool = "inSchool",
    Break = "break",
    Checkout = "checkOut",
    Missed = "missed"
}

const initialDefaultFilter = {
    programVersions: [],
    filterEventType: undefined,
    campuses: [],
    instructors: [],
    startingTerms: [],
    studentStatuses: [],
    programs: [],
    groups: [],
    phases: [],
    shifts: [],
    isMakeup: false,
    distEdEnabled: false,
    isSpecialEventEnabled: null,
    filterCheckin: DailyAttendanceFilterCheckin.None,
    filterCheckout: DailyAttendanceFilterCheckout.None
}

function DailyAttendanceView(props) {
    const {
        data,
        page,
        total,
        pageSize,
        columns,
        fields,
        getCurrentData,
        allFields,
        isLoading,
        fameSettings,
        orderField,
        defaultStudentStatuses = [],
        dispatch,
        filter,
        type,
        setDateRange,
        dateRange,
        isLoadedTableFuncs,
        overrideFilterParams,
        filterKeyOverride,
        hideDateFilter,
        hideFilter
    } = props

    const model = useModel()
    const userCampuses = model.getCurrentUserProfile()?.campuses ?? []
    const {studentProfileId, statsInfo = [], showAll = false, isErrorLog = false} = props

    const defaultFilter = useMemo(() => {
        return {
            ...initialDefaultFilter,
            studentStatuses: defaultStudentStatuses,
            campuses: userCampuses,
            includeDeletedAttendance:
                overrideFilterParams?.includeDeletedAttendance === false ? false : showAll && model.isStaffOrAdmin()
        }
    }, [defaultStudentStatuses, userCampuses, showAll, overrideFilterParams])

    const getFilterMemoryKey = () => {
        if (filterKeyOverride) return filterKeyOverride
        if (isErrorLog) return FilterKey.DailyAttendanceErrorLog
        return type === DAILY_ATTENDANCE_TABS.ALL ? FilterKey.DailyAttendanceAll : FilterKey.DailyAttendanceDaily
    }

    const filterKey = getFilterMemoryKey()

    const {searchValue, currentFilter, setCurrentFilter, changeSearch, clearFilter, applyFilter} = useFilterStorage(
        filterKey,
        defaultFilter
    )

    const popupAddManualDailyAttendance = useVisible()
    const confirmVerifyPopup = useVisible()
    const [exporting, setExporting] = useState(false)
    const pdfExportRef = useRef<DailyAttendanceCheckInCheckOutPdfHandle>(null)
    const {formatTime} = useDateTimeFormatters()
    const dateFormat = model.getUserDateFormat()
    const popupEditMultipleAttendance = useVisible()

    const {eventType: filterEventType, majorVersionIds, phaseIds, shiftIds, campusIds, programIds} = filter
    const [selectedAttendance, setSelectedAttendance] = useState<{
        profileId: number
    }>()
    const [selectedAttendances, setSelectedAttendances] = useState<any>({})
    const selectedAttendancesRef = useRef<any>({})
    const [searchDebounce, setSearchDebounce] = useState(searchValue)
    const showAttendanceDetailsPopup = useVisible(false) // Combine with showAll flag to show attendance details or daily attendance popup
    const currentProfilePermissions = useCurrentProfilePermissions()

    useDebounce(() => setSearchDebounce(searchValue), 500, [searchValue])

    const {refetch: refetchDailyAttendance} = useQuery(
        [
            showAll ? "show-all-daily-attendance" : "show-daily-attendance-by-date",
            filter,
            page,
            pageSize,
            searchDebounce,
            dateRange,
            orderField?.field,
            orderField?.order,
            fameSettings
        ],
        async () => {
            dispatch({isLoading: true})
            const params = getFilterParams()
            const fetchFunction = showAll
                ? attendanceService.getDailyAttendanceList
                : attendanceService.getDailyAttendanceListByDate
            const {data, total} = await fetchFunction({
                range: {
                    page,
                    pageSize
                },
                sort: orderField?.field
                    ? {
                          orderBy: orderField.field,
                          orderDir: orderField.order
                      }
                    : undefined,
                ...params
            })
            const dailyAttendance = (data || []).map((daily) => {
                return normalizeItem(daily)
            })
            return {
                data: dailyAttendance,
                total
            }
        },
        {
            enabled: !!isLoadedTableFuncs,
            onSuccess: ({data, total}) => {
                dispatch({data, total, isLoading: false})
            },
            onError: handleError
        }
    )

    useEffect(() => {
        props.dispatch({isClassComponent: false})
        props.dispatchFunc([
            {key: "getFilterMemoryKey", func: getFilterMemoryKey},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
    }, [isErrorLog])

    const onRefreshData = useCallback(() => {
        getData()
    }, [page, pageSize, filter, searchDebounce, orderField])

    const getFilterParams = () => {
        const currentPhaseIds = statsInfo.map((info) => info.phaseId).filter(Boolean)
        const currentShiftIds = statsInfo.map((info) => info.shiftId).filter(Boolean)
        const currentProgramIds = statsInfo.map((info) => info.id).filter(Boolean)
        const currentCampusIds = statsInfo.map((info) => info.campusId).filter(Boolean)

        const _phaseIds = phaseIds?.filter((phaseId) => currentPhaseIds.includes(phaseId)) ?? []
        const _shiftIds = shiftIds?.filter((shiftId) => currentShiftIds.includes(shiftId)) ?? []
        const _campusIds = campusIds?.filter((campusId) => currentCampusIds.includes(campusId)) ?? []
        const _programIds = programIds?.filter((programId) => currentProgramIds.includes(programId)) ?? []
        const params: any = {
            includeDeletedAttendance: !!currentFilter.includeDeletedAttendance,
            campusIds: !isEmpty(currentFilter.campuses)
                ? currentFilter.campuses.map((campus) => campus.id).concat(_campusIds)
                : campusIds,
            instructorProfileIds: !isEmpty(currentFilter.instructors)
                ? currentFilter.instructors.map((instructor) => instructor.profileId)
                : undefined,
            majorVersionIds: !isEmpty(currentFilter.programVersions)
                ? currentFilter.programVersions.map((pv) => pv.id)
                : majorVersionIds,
            programIds: !isEmpty(currentFilter.programs)
                ? currentFilter.programs.map((p) => p.id).concat(_programIds)
                : programIds,
            eventTypes: undefined,
            phaseIds: !isEmpty(currentFilter.phases)
                ? currentFilter.phases.map((phase) => phase.phaseId).concat(_phaseIds)
                : _phaseIds,
            shiftIds: !isEmpty(currentFilter.shifts)
                ? currentFilter.shifts.map((shift) => shift.shiftId).concat(_shiftIds)
                : _shiftIds,
            startingTermIds: !isEmpty(currentFilter.startingTerms)
                ? currentFilter.startingTerms.map((term) => term.id)
                : undefined,
            currentTermIds: !isEmpty(currentFilter?.currentTerms)
                ? currentFilter.currentTerms.map((term) => term.id)
                : undefined,
            statusIds: !isEmpty(currentFilter.studentStatuses)
                ? currentFilter.studentStatuses.map((status) => status.statusId)
                : undefined,
            groupIds: !isEmpty(currentFilter.groups) ? currentFilter.groups.map((group) => group.id) : undefined,
            hasCheckin: !isErrorLog && currentFilter.filterCheckin === DailyAttendanceFilterCheckin.HasCheckin,
            hasCheckout: !isErrorLog && currentFilter.filterCheckout === DailyAttendanceFilterCheckout.HasCheckout,
            isMissingCheckout:
                !isErrorLog && currentFilter.filterCheckout === DailyAttendanceFilterCheckout.MissingCheckout,
            isMissingCheckin:
                !isErrorLog && currentFilter.filterCheckin === DailyAttendanceFilterCheckin.MissingCheckin,
            isOverlappingTime: !isErrorLog && !!currentFilter.overlappingTime,
            isLateCheckin: !isErrorLog && currentFilter.filterCheckin === DailyAttendanceFilterCheckin.LateCheckin,
            isMakeup: !isErrorLog && !!currentFilter.isMakeup,
            distEdEnabled: !isErrorLog && !!currentFilter.distEdEnabled,
            isSpecialEventEnabled:
                !isErrorLog && typeof currentFilter.isSpecialEventEnabled === "boolean"
                    ? currentFilter.isSpecialEventEnabled
                    : undefined,
            hasTriangleAlert: !isErrorLog && !!currentFilter.hasTriangleAlert,
            isModified: !isErrorLog && !!currentFilter.isModified,
            isMissingFameId: !isErrorLog && !!currentFilter.isMissingFameId
        }
        if (dateRange?.length) {
            const startDate = moment(dateRange[0])
            startDate.startOf("day")
            const endDate = moment(dateRange[1])
            endDate.endOf("day")
            params.fromDate = startDate.format("YYYY-MM-DD")
            params.toDate = endDate.format("YYYY-MM-DD")
        }

        if (searchDebounce?.length) {
            params.search = searchDebounce
        }

        if ((_phaseIds?.length || _shiftIds?.length || _programIds?.length || _campusIds?.length) && filterEventType) {
            if (filterEventType === FilterEventType.Active) {
                params.eventTypes = []
            } else if (filterEventType === FilterEventType.Break) {
                params.eventTypes = [AttendanceEventType.BreakStart]
            } else if (filterEventType === FilterEventType.Checkout) {
                params.eventTypes = [AttendanceEventType.Checkout]
            } else if (filterEventType === FilterEventType.InSchool) {
                params.eventTypes = [AttendanceEventType.Checkin, AttendanceEventType.BreakEnd]
            } else if (filterEventType === FilterEventType.Missed) {
                params.isMissed = true
            }
        }

        const profileId = model.profileId

        if (!model.isStaffOrAdmin() && profileId) {
            params.studentProfileIds = [profileId]
        } else if (studentProfileId) {
            params.studentProfileIds = [parseInt(studentProfileId)]
        }

        if (overrideFilterParams) {
            return {
                ...params,
                ...overrideFilterParams
            }
        }

        if (isErrorLog) {
            params.showErrorLog = true
        }

        return params
    }

    const renderIconAttendance = (eventType: string) => {
        if (eventType === "check-out") {
            return <Icon icon="CHECKOUT_FILL" color={"#1E90FF"} className={styles.checkOutIcon} />
        } else if (eventType === "check-in") {
            return <Icon icon="CHECKIN_FILL" color={"#1E90FF"} className={styles.rightIcon} />
        } else if (eventType === "break-start") {
            return <Icon icon="ATTENDANCE_OUT" color={"#DF1642"} className={styles.rightIcon} />
        } else if (eventType === "break-end") {
            return <Icon icon="ATTENDANCE_IN" color={"#18A957"} className={styles.rightIcon} />
        } else {
            return null
        }
    }

    const clearAllSelectedAttendance = () => {
        const tableData = getCurrentData()
        const newTableData = tableData.map((item) => ({...item, isChecked: false}))
        selectedAttendancesRef.current = {}
        setSelectedAttendances({})
        props.onUpdateTableData(newTableData)
    }

    async function getData() {
        refetchDailyAttendance()
    }

    const renderBreakEvents = (breakEvents: any[], timezone) => {
        return (
            <div className={dailyAttendanceStyles.eventsContainer}>
                {breakEvents?.map((event, index) => {
                    return (
                        <>
                            <div className={dailyAttendanceStyles.event}>
                                {event.eventType === AttendanceEventType.Checkin ? (
                                    <Icon
                                        icon="CHECKIN_FILL"
                                        className={dailyAttendanceStyles.eventIcon}
                                        color={"#18a957"}
                                    />
                                ) : null}
                                {event.eventType === AttendanceEventType.Checkout ? (
                                    <Icon
                                        icon="CHECKOUT_FILL"
                                        className={dailyAttendanceStyles.eventIcon}
                                        color={"#DF1642"}
                                    />
                                ) : null}
                                {event.eventType === AttendanceEventType.BreakEnd ? (
                                    <Icon
                                        icon="ATTENDANCE_IN"
                                        className={dailyAttendanceStyles.eventIcon}
                                        color={"#18a957"}
                                    />
                                ) : null}
                                {event.eventType === AttendanceEventType.BreakStart ? (
                                    <Icon
                                        icon="ATTENDANCE_OUT"
                                        className={dailyAttendanceStyles.eventIcon}
                                        color={"#DF1642"}
                                    />
                                ) : null}
                                <span className={dailyAttendanceStyles.eventTime}>
                                    {moment(event.createdAt).tz(timezone).format(model.getUserTimeFormat())}
                                </span>
                            </div>
                            {index !== breakEvents?.length - 1 ? (
                                <div className={dailyAttendanceStyles.eventSeparator} />
                            ) : null}
                        </>
                    )
                })}
            </div>
        )
    }

    const renderBreakEventsRoundingTime = (breakEvents: any[], timezone) => {
        return (
            <div className={dailyAttendanceStyles.eventsContainer}>
                {breakEvents?.map((event, index) => {
                    return (
                        <>
                            <div className={dailyAttendanceStyles.event}>
                                {event.eventType === AttendanceEventType.Checkin ? (
                                    <Icon
                                        icon="CHECKIN_FILL"
                                        className={dailyAttendanceStyles.eventIcon}
                                        color={"#18a957"}
                                    />
                                ) : null}
                                {event.eventType === AttendanceEventType.Checkout ? (
                                    <Icon
                                        icon="CHECKOUT_FILL"
                                        className={dailyAttendanceStyles.eventIcon}
                                        color={"#DF1642"}
                                    />
                                ) : null}
                                {event.eventType === AttendanceEventType.BreakEnd ? (
                                    <Icon
                                        icon="ATTENDANCE_IN"
                                        className={dailyAttendanceStyles.eventIcon}
                                        color={"#18a957"}
                                    />
                                ) : null}
                                {event.eventType === AttendanceEventType.BreakStart ? (
                                    <Icon
                                        icon="ATTENDANCE_OUT"
                                        className={dailyAttendanceStyles.eventIcon}
                                        color={"#DF1642"}
                                    />
                                ) : null}
                                <span className={dailyAttendanceStyles.eventTime}>
                                    {moment(event.dateTimeRounded).tz(timezone).format(model.getUserTimeFormat())}
                                </span>
                            </div>
                            {index !== breakEvents?.length - 1 ? (
                                <div className={dailyAttendanceStyles.eventSeparator} />
                            ) : null}
                        </>
                    )
                })}
            </div>
        )
    }

    const normalizeItem = (daily, isExport: boolean = false) => {
        const dailyAttendanceTimeScale = fameSettings?.dailyAttendanceTimeScale ?? DailyAttendanceTimeScale.Minutes
        let events: any[] = daily.events ?? []
        if (!showAll) {
            events = (daily.attendances || [])
                .reduce((acc, cur) => acc.concat(...(cur.events || [])), [])
                .sort((a, b) => moment.utc(a.createdAt).diff(moment.utc(b.createdAt)))
            if (daily.attendances?.length === 1) {
                daily.isMakeup = daily.attendances[0].isMakeup
                daily.approvalIsRequired = !!daily.attendances[0].approvalIsRequired
                daily.distEdEnabled = !!daily.attendances[0].distEdEnabled
                daily.isFlexShift = !!daily.attendances[0].isFlexShift
            }
        }
        const campusTimezone = daily?.student?.timezone ?? moment.tz.guess()
        const latestEventType = events.length ? events[events.length - 1].eventType : undefined
        const checkInEvents = events.filter((event) => event.eventType === AttendanceEventType.Checkin)
        const breakEvents = events.filter((event) =>
            [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(event.eventType)
        )
        const checkIn = checkInEvents.length ? checkInEvents[0] : undefined
        const checkOutEvents = events.filter((event) => event.eventType === AttendanceEventType.Checkout)
        const checkOut = checkOutEvents.length ? checkOutEvents[checkOutEvents.length - 1] : undefined
        const missedHours = Math.max(getHoursPerDay(daily), 0)

        const attendanceId = daily.id
        const checkInTime = checkIn ? formatTime(convertTZ(new Date(checkIn.createdAt), campusTimezone)) : null
        const checkInDateTimeRounded = checkIn
            ? formatTime(convertTZ(new Date(checkIn.dateTimeRounded), campusTimezone))
            : null
        const checkOutTime = checkOut ? formatTime(convertTZ(new Date(checkOut.createdAt), campusTimezone)) : null
        const checkOutDateTimeRounded = checkOut
            ? formatTime(convertTZ(new Date(checkOut.dateTimeRounded), campusTimezone))
            : checkOut
        const shifts: any[] = uniqBy(daily.student?.shifts ?? [], "shiftId")
        const phases: any[] = uniqBy(daily.student?.phases ?? [], "phaseId")

        const statusCanEditAttd = get(daily, ["student", "status", "allowsDailyAttendanceManagement"], true)
        const {canEdit} = getDailyAttendancePermissions({
            currentProfilePermissions,
            isLocked: daily.isLocked,
            deletedAt: daily.deletedAt,
            allowsDailyAttendanceManagement: statusCanEditAttd
        })

        let result = {
            ...daily,
            attendanceId,
            isChecked: !!selectedAttendancesRef.current[attendanceId],
            phaseName: phases.map((phase) => phase.name).join(",") ?? "",
            shiftName: shifts.map((shift) => shift.name).join(",") ?? "",
            id: uuid(),
            checkInHtml: checkInTime,
            breakTimeHtml: renderBreakEvents(breakEvents, campusTimezone),
            breakRoundingTimeHtml: renderBreakEventsRoundingTime(breakEvents, campusTimezone),
            checkOutHtml: checkOutTime,
            checkInDateTimeRoundedHtml: checkInDateTimeRounded,
            checkOutDateTimeRoundedHtml: checkOutDateTimeRounded,
            statusHtml: isExport ? latestEventType : renderIconAttendance(latestEventType),
            makeupHtml: <Checkbox checked={daily.isMakeup} disabled />,
            locationHtml: checkIn?.resource?.name ?? "",
            missedHours: formatDailyAttendanceHours(missedHours, dailyAttendanceTimeScale),
            makeUpHours: formatDailyAttendanceHours(daily.makeUpHours, dailyAttendanceTimeScale),
            attendedHours: formatDailyAttendanceHours(daily.attendedHours, dailyAttendanceTimeScale),
            distEdHours: formatDailyAttendanceHours(daily.distEdHours, dailyAttendanceTimeScale),
            scheduleHours: formatDailyAttendanceHours(daily.scheduleHours, dailyAttendanceTimeScale),
            accommodatedHoursHtml: <Checkbox checked={daily.student?.isAccommodatedHoursEnabled} disabled />,
            approvalIsRequiredHtml: <Checkbox checked={!!daily.approvalIsRequired} disabled />,
            distEdHtml: <Checkbox checked={!!daily.distEdEnabled} disabled />,
            flexShiftHtml: <Checkbox checked={!!daily.isFlexShift} disabled />,
            attendHtml: isExport ? renderAttendExport(daily) : renderAttendHtml(daily),
            syncErrorHtml: events.map((e) => e.syncError).join("\n")
        }

        result.isCheckboxDisabled = !showAll || !canEdit

        result = {
            ...result,
            isOpenChild: false,
            majorVersionName: daily.majorVersion?.versionName ?? "",
            majorVersionCode: daily.majorVersion?.versionCode ?? "",
            dateHtml: isExport ? formatDateOnly(daily.date, dateFormat) : renderDateHtml(result),
            customProfileId: daily.student?.customProfileId ?? "",
            instructorNames: (daily.instructors ?? [])
                .map(function (instructor) {
                    return instructor.name
                })
                .join(","),
            accommodatedHoursHtml: <Checkbox checked={daily.student?.isAccommodatedHoursEnabled} disabled />,
            majorName: daily.majorVersion?.majorName ?? "",
            majorCode: daily.majorVersion?.majorCode ?? "",
            studentName: getFullName(daily.student),
            makeupHtml: <Checkbox checked={daily.isMakeup} disabled />,
            distEdHtml: <Checkbox checked={!!daily.distEdEnabled} disabled />,
            flexShiftHtml: <Checkbox checked={!!daily.isFlexShift} disabled />,
            approvalIsRequiredHtml: <Checkbox checked={!!daily.approvalIsRequired} disabled />,
            rowClassName: !!daily.deletedAt ? dailyAttendanceStyles.disabledRow : null
        }

        return result
    }

    const getHoursPerDay = (item) => {
        if (model.slug === "ogleschool") {
            return (item.student?.overallStats?.dailyScheduledHours || item.missedHours) - (item.attendedHours ?? 0)
        } else {
            return item.missedHours ?? 0
        }
    }

    function hasBreakEndBeforeBreakStart(events: any[]) {
        const sortedByTimeOnly = events.sort((a, b) => {
            return moment(a.createdAt).diff(moment(b.createdAt))
        })

        let foundBreakStart = false
        for (const event of sortedByTimeOnly) {
            if (event.eventType === "break-start") {
                foundBreakStart = true
            }
            if (event.eventType === "break-end") {
                if (!foundBreakStart) {
                    return true
                }
                foundBreakStart = false
            }
        }
        return false
    }

    const renderAttendExport = (daily: any) => {
        if (!showAll) {
            const attendances = daily.attendances ?? []
            const status = attendances.every((attendance) => attendance.status === AttendanceStatus.Approved)
            const isVerifiedAll = attendances.every((attendance) => attendance.isVerified === "yes")

            return status === AttendanceStatus.Approved
                ? `${YesNo.Yes} (Verified: ${isVerifiedAll ? YesNo.Yes : YesNo.No})`
                : `${YesNo.No} (Verified: ${isVerifiedAll ? YesNo.Yes : YesNo.No})`
        }

        const events = daily.events ?? []
        const checkinEvents = events.filter((event) => event.eventType === AttendanceEventType.Checkin)
        const checkoutEvents = events.filter((event) => event.eventType === AttendanceEventType.Checkout)
        const breakStartEvents = events.filter((event) => event.eventType === AttendanceEventType.BreakStart)
        const breakEndEvents = events.filter((event) => event.eventType === AttendanceEventType.BreakEnd)
        const totalBreakStart = breakStartEvents?.length ?? 0
        const totalBreakEnd = breakEndEvents?.length ?? 0
        const isBreakEndBeforeBreakStart = hasBreakEndBeforeBreakStart(
            events.filter((item) =>
                [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(item.eventType)
            )
        )

        if (
            !checkinEvents?.length ||
            !checkoutEvents?.length ||
            totalBreakStart !== totalBreakEnd ||
            isBreakEndBeforeBreakStart
        ) {
            return "Warning"
        }

        return daily.status === AttendanceStatus.Approved
            ? `${YesNo.Yes} (Verified: ${daily.isVerified ? YesNo.Yes : YesNo.No})`
            : `${YesNo.No} (Verified: ${daily.isVerified ? YesNo.Yes : YesNo.No})`
    }

    function renderAttendHtml(daily: any) {
        if (!showAll) {
            const attendances = daily.attendances ?? []
            const isRejectedAll =
                attendances.every((attendance) => attendance.isVerified === "yes") &&
                attendances.every((attendance) => attendance.status === AttendanceStatus.Rejected)

            const isVerifiedAll = !isRejectedAll && attendances.every((attendance) => attendance.isVerified === "yes")

            for (let attendance of attendances) {
                if (attendance.status === AttendanceStatus.Irregular) {
                    return (
                        <div className={styles.actionWarning}>
                            <Icon icon="WARNING_TRIANGLE_FILL" />
                        </div>
                    )
                }
                const events = attendance.events ?? []
                const hasEventNotSync = !!events.find((event) => !event.synced)
                const checkinEvents = events.filter((event) => event.eventType === AttendanceEventType.Checkin)
                const checkoutEvents = events.filter((event) => event.eventType === AttendanceEventType.Checkout)
                const breakStartEvents = events.filter((event) => event.eventType === AttendanceEventType.BreakStart)
                const breakEndEvents = events.filter((event) => event.eventType === AttendanceEventType.BreakEnd)
                const totalBreakStart = breakStartEvents?.length ?? 0
                const totalBreakEnd = breakEndEvents?.length ?? 0
                const isBreakEndBeforeBreakStart = hasBreakEndBeforeBreakStart(
                    events.filter((item) =>
                        [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(item.eventType)
                    )
                )

                if (fameSettings?.fameFreedomEnabled && hasEventNotSync) {
                    // return (
                    //     <div className={styles.actionWarning}>
                    //         <Icon icon="SYNC_ERROR" />
                    //     </div>
                    // )
                } else if (fameSettings?.fameAdvantageEnabled) {
                    if (attendance.isMakeup && attendance.isVerified === YesNo.No) {
                        return (
                            <div className={styles.actionWarning}>
                                <Icon icon="PERSON_CROSS" />
                            </div>
                        )
                    }

                    if (attendance.isVerified === YesNo.No) {
                        return (
                            <div className={styles.actionWarning}>
                                <Icon icon="WARNING_TRIANGLE_FILL" />
                            </div>
                        )
                    }

                    if (
                        hasEventNotSync &&
                        attendance.isVerified === YesNo.Yes &&
                        attendance.status === AttendanceStatus.Approved
                    ) {
                        // return (
                        //     <div className={styles.actionWarning}>
                        //         <Icon icon="SYNC_ERROR" />
                        //     </div>
                        // )
                    }
                }

                if (attendance.isMakeup && attendance.isVerified === YesNo.No) {
                    return (
                        <div className={styles.actionWarning}>
                            <Icon icon="PERSON_CROSS" />
                        </div>
                    )
                }

                if (
                    !checkinEvents?.length ||
                    !checkoutEvents?.length ||
                    totalBreakStart !== totalBreakEnd ||
                    isBreakEndBeforeBreakStart ||
                    attendance.isVerified === YesNo.No
                ) {
                    return (
                        <div className={styles.actionWarning}>
                            <Icon icon="WARNING_TRIANGLE_FILL" />
                        </div>
                    )
                }
            }

            if (isRejectedAll) {
                return (
                    <div className={styles.attClose}>
                        <Icon icon="CROSS_CIRCLE" />
                    </div>
                )
            }

            if (isVerifiedAll) {
                return (
                    <div className={classroomAttendanceStyles.attCheckVerified}>
                        <Icon icon="CHECKED" />
                    </div>
                )
            }

            return (
                <div className={styles.attCheck}>
                    <Icon icon="CHECKED" />
                </div>
            )
        }

        const status = daily.status
        if (status === AttendanceStatus.Irregular) {
            return (
                <div className={styles.actionWarning}>
                    <Icon icon="WARNING_TRIANGLE_FILL" />
                </div>
            )
        }
        const isVerified = daily.isVerified
        const events = daily.events ?? []
        const hasEventNotSync = !!events.find((event) => !event.synced)
        const checkinEvents = events.filter((event) => event.eventType === AttendanceEventType.Checkin)
        const checkoutEvents = events.filter((event) => event.eventType === AttendanceEventType.Checkout)
        const breakStartEvents = events.filter((event) => event.eventType === AttendanceEventType.BreakStart)
        const breakEndEvents = events.filter((event) => event.eventType === AttendanceEventType.BreakEnd)
        const totalBreakStart = breakStartEvents?.length ?? 0
        const totalBreakEnd = breakEndEvents?.length ?? 0
        const isBreakEndBeforeBreakStart = hasBreakEndBeforeBreakStart(
            events.filter((item) =>
                [AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(item.eventType)
            )
        )

        if (fameSettings?.fameFreedomEnabled && hasEventNotSync) {
            // return (
            //     <div className={styles.actionWarning}>
            //         <Icon icon="SYNC_ERROR" />
            //     </div>
            // )
        } else if (fameSettings?.fameAdvantageEnabled) {
            if (daily.isMakeup && daily.isVerified === YesNo.No) {
                return (
                    <div className={styles.actionWarning}>
                        <Icon icon="PERSON_CROSS" />
                    </div>
                )
            }

            if (daily.isVerified === YesNo.No) {
                return (
                    <div className={styles.actionWarning}>
                        <Icon icon="WARNING_TRIANGLE_FILL" />
                    </div>
                )
            }

            if (hasEventNotSync && daily.isVerified === YesNo.Yes && daily.status === AttendanceStatus.Approved) {
                // return (
                //     <div className={styles.actionWarning}>
                //         <Icon icon="SYNC_ERROR" />
                //     </div>
                // )
            }
        }

        if (daily.isMakeup && daily.isVerified === YesNo.No) {
            return (
                <div className={styles.actionWarning}>
                    <Icon icon="PERSON_CROSS" />
                </div>
            )
        }

        if (
            !checkinEvents?.length ||
            !checkoutEvents?.length ||
            totalBreakStart !== totalBreakEnd ||
            isBreakEndBeforeBreakStart ||
            isVerified === YesNo.No
        ) {
            return (
                <div className={styles.actionWarning}>
                    <Icon icon="WARNING_TRIANGLE_FILL" />
                </div>
            )
        }

        if (isVerified === "yes" && status === AttendanceStatus.Rejected) {
            return (
                <div className={styles.attClose}>
                    <Icon icon="CROSS_CIRCLE" />
                </div>
            )
        }
        if (isVerified === "yes") {
            return (
                <div className={classroomAttendanceStyles.attCheckVerified}>
                    <Icon icon="CHECKED" />
                </div>
            )
        }
        switch (status) {
            case AttendanceStatus.Approved:
                return (
                    <div className={styles.attCheck}>
                        <Icon icon="CHECKED" />
                    </div>
                )
            case AttendanceStatus.Irregular:
                return (
                    <div className={styles.actionWarning}>
                        <Icon icon="WARNING_TRIANGLE_FILL" />
                    </div>
                )
            case AttendanceStatus.Rejected:
                return (
                    <div className={styles.attClose}>
                        <Icon icon="CROSS_CIRCLE" />
                    </div>
                )
            case AttendanceStatus.InClass:
            default:
                return null
        }
    }

    function getColumns() {
        if (type === DAILY_ATTENDANCE_TABS.ALL) {
            return [
                {
                    title: "Date",
                    field: "dateHtml",
                    headerStyle: {minWidth: "150px"},
                    sortable: true,
                    orderField: "attendanceDate",
                    columnIndex: 1
                },
                {
                    title: "Sis Attn Id",
                    field: "sisid",
                    headerStyle: {minWidth: "120px"},
                    columnIndex: 2
                },
                {
                    title: "Student Id",
                    field: "customProfileId",
                    headerStyle: {minWidth: "120px"},
                    columnIndex: 3
                },

                {
                    title: "Student Name",
                    field: "studentName",
                    headerStyle: {minWidth: "150px"},
                    sortable: true,
                    orderField: "fullName",
                    columnIndex: 4
                },

                {
                    title: "Check In Time",
                    field: "checkInHtml",
                    headerStyle: {minWidth: "120px"},
                    sortable: true,
                    orderField: "checkinTime",
                    columnIndex: 5
                },
                {
                    title: "Break Time",
                    field: "breakTimeHtml",
                    columnIndex: 6
                },
                {
                    title: "Check Out Time",
                    field: "checkOutHtml",
                    headerStyle: {minWidth: "130px"},
                    sortable: true,
                    orderField: "checkoutTime",
                    columnIndex: 7
                },
                {
                    title: "Check In Rounding Time",
                    field: "checkInDateTimeRoundedHtml",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 8
                },
                {
                    title: "Break Rounding Time",
                    field: "breakRoundingTimeHtml",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 9
                },
                {
                    title: "Check Out Rounding Time",
                    field: "checkOutDateTimeRoundedHtml",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 10
                },
                {
                    title: "Status",
                    field: "statusHtml",
                    headerStyle: {minWidth: "80px"},
                    columnIndex: 11
                },
                {
                    title: "Accommodated Hours",
                    field: "accommodatedHoursHtml",
                    headerStyle: {width: "100px"},
                    columnIndex: 12,
                    sortable: true,
                    orderField: "isAccommodatedHoursEnabled"
                },
                {
                    title: "Approval Is Required",
                    field: "approvalIsRequiredHtml",
                    headerStyle: {minWidth: "100px"},
                    sortable: true,
                    orderField: "approvalIsRequired",
                    columnIndex: 13
                },
                {
                    title: "DistEd",
                    field: "distEdHtml",
                    headerStyle: {minWidth: "70px"},
                    sortable: true,
                    orderField: "distEdEnabled",
                    columnIndex: 14
                },
                {
                    title: "Flex Shift",
                    field: "flexShiftHtml",
                    headerStyle: {minWidth: "70px"},
                    sortable: true,
                    orderField: "isFlexShift",
                    columnIndex: 15
                },
                {
                    title: "Makeup",
                    field: "makeupHtml",
                    headerStyle: {minWidth: "70px"},
                    sortable: true,
                    orderField: "isMakeup",
                    columnIndex: 16
                },
                {
                    title: "Location",
                    field: "locationHtml",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 17
                },
                {
                    title: "Attended",
                    field: "attendedHours",
                    headerStyle: {minWidth: "70px"},
                    sortable: true,
                    orderField: "attendedHours",
                    columnIndex: 18
                },
                {
                    title: "Missed",
                    field: "missedHours",
                    headerStyle: {minWidth: "70px"},
                    sortable: true,
                    orderField: "missedHours",
                    columnIndex: 19
                },
                {
                    title: "Phase",
                    field: "phaseName",
                    headerStyle: {minWidth: "120px"},
                    columnIndex: 20
                },
                {
                    title: "Shift",
                    field: "shiftName",
                    headerStyle: {minWidth: "120px"},
                    columnIndex: 21
                },
                {
                    title: "Major Code",
                    field: "majorCode",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 22
                },
                {
                    title: "Major Name",
                    field: "majorName",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 23
                },
                {
                    title: "Major Version",
                    field: "majorVersionName",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 24
                },
                {
                    title: "Major Version Code",
                    field: "majorVersionCode",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 25
                },
                {
                    title: "attn",
                    field: "attendHtml",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 26
                },
                {
                    title: "Sync Error",
                    field: "syncErrorHtml",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 27
                }
            ]
        } else {
            const columns = [
                {
                    title: "Date",
                    field: "dateHtml",
                    headerStyle: {minWidth: "150px"},
                    sortable: true,
                    orderField: "attendanceDate",
                    columnIndex: 1
                },
                {
                    title: "Sis Attn Id",
                    field: "sisid",
                    headerStyle: {minWidth: "120px"},
                    columnIndex: 2
                },
                {
                    title: "Student Id",
                    field: "customProfileId",
                    headerStyle: {minWidth: "120px"},
                    columnIndex: 3
                },

                {
                    title: "Student Name",
                    field: "studentName",
                    headerStyle: {minWidth: "150px"},
                    sortable: true,
                    orderField: "fullName",
                    columnIndex: 4
                },

                {
                    title: "Check In Time",
                    field: "checkInHtml",
                    headerStyle: {minWidth: "120px"},
                    sortable: true,
                    orderField: "checkinTime",
                    columnIndex: 5
                },
                {
                    title: "Break Time",
                    field: "breakTimeHtml",
                    columnIndex: 6
                },
                {
                    title: "Check Out Time",
                    field: "checkOutHtml",
                    headerStyle: {minWidth: "130px"},
                    sortable: true,
                    orderField: "checkoutTime",
                    columnIndex: 7
                },
                {
                    title: "Check In Rounding Time",
                    field: "checkInDateTimeRoundedHtml",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 8
                },
                {
                    title: "Break Rounding Time",
                    field: "breakRoundingTimeHtml",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 9
                },
                {
                    title: "Check Out Rounding Time",
                    field: "checkOutDateTimeRoundedHtml",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 10
                },
                {
                    title: "Status",
                    field: "statusHtml",
                    headerStyle: {minWidth: "80px"},
                    columnIndex: 11
                },

                {
                    title: "Attended",
                    field: "attendedHours",
                    headerStyle: {minWidth: "70px"},
                    sortable: true,
                    orderField: "attendedHours",
                    columnIndex: 12
                },
                {
                    title: "Missed",
                    field: "missedHours",
                    headerStyle: {minWidth: "70px"},
                    sortable: true,
                    orderField: "missedHours",
                    columnIndex: 13
                },
                {
                    title: "Makeup",
                    field: "makeUpHours",
                    headerStyle: {minWidth: "70px"},
                    sortable: true,
                    orderField: "makeUpHours",
                    columnIndex: 14
                },
                {
                    title: "DistEd",
                    field: "distEdHours",
                    headerStyle: {minWidth: "70px"},
                    sortable: true,
                    orderField: "distEdHours",
                    columnIndex: 15
                },
                {
                    title: "Schedule",
                    field: "scheduleHours",
                    headerStyle: {minWidth: "70px"},
                    sortable: true,
                    orderField: "scheduleHours",
                    columnIndex: 16
                },
                {
                    title: "Major Code",
                    field: "majorCode",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 17
                },
                {
                    title: "Major Name",
                    field: "majorName",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 18
                },
                {
                    title: "Major Version",
                    field: "majorVersionName",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 19
                },
                {
                    title: "Major Version Code",
                    field: "majorVersionCode",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 20
                },
                {
                    title: "attn",
                    field: "attendHtml",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 21
                }
            ]

            if (isErrorLog) {
                columns.push({
                    title: "Sync Error",
                    field: "syncErrorHtml",
                    headerStyle: {minWidth: "150px"},
                    columnIndex: 22
                })
            }
            return columns
        }
    }

    function getFields() {
        if (type === DAILY_ATTENDANCE_TABS.ALL) {
            return [
                "Date",
                // "Sis Attn Id",
                "Student Id",
                "Student Name",
                "Check In Time",
                "Break Time",
                "Check Out Time",
                // "Check In Rounding Time",
                // "Check Out Rounding Time",
                "Status",
                "Accommodated Hours",
                "Approval Is Required",
                "DistEd",
                "Flex Shift",
                "Makeup",
                "Location",
                "Attended",
                "Missed",
                "Phase",
                "Shift",
                "Major Code",
                "Major Name",
                "Major Version",
                "Major Version Code",
                "attn"
            ]
        } else {
            if (isErrorLog) {
                return [
                    "Date",
                    "Student Id",
                    "Student Name",
                    "Check In Time",
                    "Break Time",
                    "Check Out Time",
                    "Status",
                    "Attended",
                    "Missed",
                    "Makeup",
                    "Schedule",
                    "attn",
                    "Sync Error"
                ]
            }

            return [
                "Date",
                "Student Id",
                "Student Name",
                "Check In Time",
                "Break Time",
                "Check Out Time",
                "Status",
                "Attended",
                "Missed",
                "Makeup",
                "DistEd",
                "Schedule",
                "Major Code",
                "Major Name",
                "Major Version",
                "Major Version Code",
                "attn"
            ]
        }
    }

    const onClickRow = async (data) => {
        showAttendanceDetailsPopup.open()
        try {
            setSelectedAttendance({...data, id: data.attendanceId})
        } catch (err) {
            handleError(err)
        }
    }

    const onClosePopup = useCallback(() => {
        showAttendanceDetailsPopup.close()
        setSelectedAttendance(undefined)
    }, [])

    const onChangeFilter = (key, value) => {
        const newFilter = {...currentFilter}
        newFilter[key] = value
        setCurrentFilter(newFilter)
    }

    const onApplyFilter = () => {
        applyFilter()
        getData()
    }

    const handleExportFile = async (type: ExportFileType) => {
        setExporting(true)
        try {
            const params = getFilterParams()

            const fetchFunction = showAll
                ? attendanceService.getDailyAttendanceList
                : attendanceService.getDailyAttendanceListByDate

            const {data} = await fetchFunction({
                range: {
                    page: 1,
                    pageSize: total
                },
                ...params
            })
            if (type === "pdf") {
                const [from, to] = dateRange ?? []
                if (pdfExportRef.current) {
                    pdfExportRef.current.print({
                        students: (data || []).map((row) => mapAttendanceRowToCheckInCheckOutStudent(row)),
                        dateLabel: formatCheckInCheckOutDateLabel(from, to, dateFormat),
                        campuses: getSelectedFilterNames(currentFilter?.campuses),
                        programs: getSelectedFilterNames(currentFilter?.programs),
                        phases: getSelectedFilterNames(currentFilter?.phases),
                        shifts: getSelectedFilterNames(currentFilter?.shifts)
                    })
                } else {
                    setExporting(false)
                }
                return
            }
            const dailyAttendance = (data || []).map((daily) => {
                return normalizeItem(daily, true)
            })
            const columnsExport = columns.filter(
                (item) => item.field !== "breakTimeHtml" && item.field !== "breakRoundingTimeHtml"
            )
            const filename = `Daily_Attendance_${moment().format("MM_DD_YYYY")}`
            const payload = {
                filename,
                columns: columnsExport.map((col) => col.title.toUpperCase()),
                rows: dailyAttendance.map((item) =>
                    columnsExport.map((col) => {
                        if ("approvalIsRequiredHtml" === col.field) {
                            return item["approvalIsRequired"] ? YesNo.Yes : YesNo.No
                        }
                        if ("distEdHtml" === col.field) {
                            return item["distEdEnabled"] ? YesNo.Yes : YesNo.No
                        }
                        if ("flexShiftHtml" === col.field) {
                            return item["isFlexShift"] ? YesNo.Yes : YesNo.No
                        }
                        if ("accommodatedHoursHtml" === col.field) {
                            return item.student?.isAccommodatedHoursEnabled ? YesNo.Yes : YesNo.No
                        }
                        if ("makeupHtml" === col.field) {
                            return item["isMakeup"] ? YesNo.Yes : YesNo.No
                        }
                        return item[col.field]?.toString() ?? ""
                    })
                )
            }
            if (type === "csv") {
                exportCsv(payload)
            } else if (type === "excel") {
                exportExcel(payload)
            }
        } catch (err) {
            handleError(err)
            setExporting(false)
        } finally {
            if (type !== "pdf") {
                setExporting(false)
            }
        }
    }

    const renderDateHtml = (daily) => {
        return <span>{formatDateOnly(daily.date, dateFormat)}</span>
    }

    const onCancelPopupEditMultiple = () => {
        popupEditMultipleAttendance.close()
    }

    const onShowPopupEditMultiple = () => {
        popupEditMultipleAttendance.open()
    }

    const onApproveAllDailyAttendance = async () => {
        const attendances: any[] = Object.values(selectedAttendances)
        const attendanceIds = attendances.map((item) => item.attendanceId)
        try {
            confirmVerifyPopup.close()
            dispatch({isLoading: true})
            await attendanceService.approveAllDailyAttendance({
                attendanceIds
            })
            clearAllSelectedAttendance()
            getData()
        } catch (err) {
            dispatch({isLoading: false})
            handleError(err)
        }
    }

    const onUpdateRowData = (updatedData) => {
        const data = getCurrentData()
        const newData = cloneDeep(data).map((item) => {
            if (item.id === updatedData.id) {
                return {
                    ...item,
                    isChecked: updatedData.isChecked
                }
            }
            return item
        })
        if (updatedData.isChecked) {
            selectedAttendancesRef.current = {
                ...selectedAttendancesRef.current,
                [updatedData.attendanceId]: updatedData
            }
        } else {
            delete selectedAttendancesRef.current[updatedData.attendanceId]
        }

        setSelectedAttendances(cloneDeep(selectedAttendancesRef.current))

        dispatch({data: newData})
    }

    const onToggleCheckHeader = (checked) => {
        const data = getCurrentData()
        const newTableData = data.map((item) => {
            if (!item.isCheckboxDisabled) {
                item.isChecked = checked
            }
            return item
        })
        dispatch({data: newTableData})
        for (let item of newTableData) {
            if (item.isChecked) {
                selectedAttendancesRef.current = {
                    ...selectedAttendancesRef.current,
                    [item.attendanceId]: item
                }
            } else {
                delete selectedAttendancesRef.current[item.attendanceId]
            }
        }

        setSelectedAttendances(cloneDeep(selectedAttendancesRef.current))
    }

    const headerActions = () => {
        const actions: any = [
            {
                title: (
                    <div className={dailyAttendanceStyles.actionWrap}>
                        <BaseButton
                            title="EDIT"
                            variant="secondary"
                            className={dailyAttendanceStyles.verifybtn}
                            onClick={onShowPopupEditMultiple}
                            loading={false}
                        />
                    </div>
                ) as any,
                icon: null,
                action: () => {}
            },
            {
                title: (
                    <div className={dailyAttendanceStyles.actionWrap}>
                        <BaseButton
                            title="APPROVE ALL"
                            variant="secondary"
                            className={dailyAttendanceStyles.approveBtn}
                            onClick={confirmVerifyPopup.open}
                            loading={false}
                        />
                    </div>
                ) as any,
                icon: null,
                action: () => {}
            },
            {
                title: (
                    <div className={dailyAttendanceStyles.actionWrap}>
                        <BaseButton
                            title={`CLEAR (${Object.keys(selectedAttendances).length} items)`}
                            variant="secondary"
                            className={dailyAttendanceStyles.clearBtn}
                            onClick={clearAllSelectedAttendance}
                            loading={false}
                        />
                    </div>
                ) as any,
                icon: null,
                action: () => {}
            }
        ]
        return actions
    }

    return (
        <div style={{paddingBottom: 150}}>
            {!hideFilter && (
                <DailyAttendanceFilter
                    searchValue={searchValue}
                    changeSearch={changeSearch}
                    onApplyFilter={onApplyFilter}
                    clearFilter={clearFilter}
                    includeDateRange
                    hideDateFilter={hideDateFilter}
                    dateRange={dateRange}
                    onChangeDateRange={setDateRange}
                    currentFilter={currentFilter}
                    enableMakeupFilter={!isErrorLog}
                    enableShowDeletedAttendanceFilter={showAll}
                    onOpenAddManualDailyAttendancePopup={popupAddManualDailyAttendance.open}
                    exporting={exporting}
                    handleExportFile={handleExportFile}
                    onChangeFilter={onChangeFilter}
                    setCurrentFilter={setCurrentFilter}
                    isErrorLog={isErrorLog}
                    savedFilterOptions={{
                        enabled: true,
                        filterKey: filterKey,
                        setFilter: setCurrentFilter,
                        defaultFilter
                    }}
                />
            )}
            <KlassappTableHeader
                page={page}
                total={total}
                defaultPageSize={pageSize}
                isShowAction={!!Object.keys(selectedAttendances).length}
                actions={headerActions()}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                isShowCheckedColumn={!!showAll}
                isShowCheckedColumnHeader={!!showAll}
                columns={columns}
                data={data}
                isLoading={isLoading}
                fields={fields}
                orderField={orderField}
                allFields={allFields}
                onClickRowItem={onClickRow}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={onUpdateRowData}
                onClickSortColumn={props.onClickSortColumn}
                onUpdateTableData={props.onUpdateTableData}
                onToggleCheckHeader={onToggleCheckHeader}
                onDraggableColumn={props.onDraggableColumn}
                onChangeAllFields={props.onChangeAllFields}
            />
            {/* Show attendance details popup */}
            <DailyAttendancePopup
                studentProfileIds={selectedAttendance?.profileId ? [selectedAttendance.profileId] : []}
                getListAttendances={getData}
                selectedAttendance={selectedAttendance}
                isShow={showAttendanceDetailsPopup.isVisible && showAll}
                onClose={onClosePopup}
            />
            {/* Show daily attendance details popup */}
            <DailyAttendanceDetailsPopup
                selectedAttendance={selectedAttendance}
                isShow={showAttendanceDetailsPopup.isVisible && !showAll}
                onClose={() => {
                    onClosePopup()
                    onRefreshData()
                }}
            />
            <PopupEditMultipleAttendance
                getListAttendances={getData}
                selectedAttendances={Object.values(selectedAttendances)}
                isShow={popupEditMultipleAttendance.isVisible}
                onClose={onCancelPopupEditMultiple}
                clearAllSelectedAttendance={clearAllSelectedAttendance}
            />
            <AddManualDailyAttendance
                onRefreshData={onRefreshData}
                onClose={() => {
                    popupAddManualDailyAttendance.close()
                    onRefreshData()
                }}
                isShow={popupAddManualDailyAttendance.isVisible}
            />

            <ConfirmPopup
                isVisible={confirmVerifyPopup.isVisible}
                onClose={confirmVerifyPopup.close}
                onConfirm={onApproveAllDailyAttendance}
                title={`Are you sure you want to approve all selected attendance records?`}
            />
            <DailyAttendanceCheckInCheckOutPdf ref={pdfExportRef} onFinished={() => setExporting(false)} />
        </div>
    )
}
export const DailyAttendance = KlassappTableHOC(DailyAttendanceView)
